import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Menu, X, Printer, Search, LogOut, Package, DollarSign, UserCircle, Sun, Moon } from 'lucide-react';
import api from '../services/api';
import { QRCodeSVG } from 'qrcode.react';

// Versión de la app para panel de Vendedor
const APP_VERSION = 'v2.0.35-FILTER-DELETED';

const SellerSalesPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [seller, setSeller] = useState(null);
  const [pointOfSale, setPointOfSale] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [generatedVoucher, setGeneratedVoucher] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Estados para modales del menú
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [bulkPlanId, setBulkPlanId] = useState('');
  const [bulkQuantity, setBulkQuantity] = useState(5);
  const [searchCode, setSearchCode] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [lastVoucher, setLastVoucher] = useState(null);

  // Colores para las tarjetas (rotación para evitar repetición)
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-red-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500'
  ];

  useEffect(() => {
    loadSellerData();
  }, []);

  const loadSellerData = async () => {
    try {
      // Obtener datos del vendedor actual
      const sellerResponse = await api.get('/api/sellers/me');
      setSeller(sellerResponse.data);

      // Obtener punto de venta asignado
      const posResponse = await api.get(`/api/points-of-sale/${sellerResponse.data.pos_id}`);
      setPointOfSale(posResponse.data);

      // Debug: ver qué planes vienen del backend
      console.log('📦 TPV Data:', posResponse.data);
      console.log('📋 Planes asignados:', posResponse.data.assigned_plans);

      // Los planes ya vienen en el TPV con sus precios - filtrar los DELETED
      const activePlans = (posResponse.data.assigned_plans || []).filter(plan => !plan.plan_name.includes('DELETED'));
      setPlans(activePlans);
    } catch (error) {
      console.error('Error loading seller data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateVoucher = async (plan) => {
    // Validar si el vendedor es "por saldo" y no tiene saldo
    if (seller?.seller_type === 'por_saldo' && seller?.current_balance <= 0) {
      alert('❌ Saldo insuficiente\n\nNo puedes generar fichas porque tu saldo es $0.\n\nContacta al administrador para que te agregue saldo.');
      return;
    }
    
    setProcessing(true);
    try {
      // Generar ficha
      const response = await api.post('/api/vouchers/generate-single', {
        plan_id: plan.plan_id,
        pos_id: pointOfSale.id
      });

      if (response.data.success) {
        setGeneratedVoucher(response.data.voucher);
        setLastVoucher(response.data.voucher); // Guardar como última ficha
        
        // Actualizar saldo del vendedor
        await loadSellerData();
        
        // Si tiene impresión directa, intentar imprimir
        if (seller.direct_print) {
          // TODO: Integrar con impresora Bluetooth
          console.log('Enviando a impresora...', response.data.voucher);
        } else {
          // Mostrar modal con la ficha
          setShowVoucherModal(true);
        }
      }
    } catch (error) {
      console.error('Error generating voucher:', error);
      alert('Error al generar ficha: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const getColorForIndex = (index) => {
    return colors[index % colors.length];
  };

  const [showPrintOptionsModal, setShowPrintOptionsModal] = useState(false);
  const [generatedBatchId, setGeneratedBatchId] = useState(null);

  const handleBulkGenerate = async () => {
    if (!bulkPlanId || bulkQuantity < 1 || bulkQuantity > 50) {
      alert('Selecciona un plan válido y cantidad entre 1 y 50');
      return;
    }

    setProcessing(true);
    try {
      const response = await api.post('/api/vouchers/generate-bulk', {
        plan_id: bulkPlanId,
        pos_id: pointOfSale.id,
        quantity: parseInt(bulkQuantity)
      });

      if (response.data.success) {
        setGeneratedBatchId(response.data.batch_id);
        setShowBulkModal(false);
        setShowPrintOptionsModal(true);
        await loadSellerData(); // Actualizar saldo
      }
    } catch (error) {
      console.error('Error generating bulk vouchers:', error);
      alert('Error al generar fichas: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const handlePrintPDF = async (size) => {
    try {
      const response = await api.get(`/api/vouchers/pdf-html/${generatedBatchId}?size=${size}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fichas_${generatedBatchId}_${size}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setShowPrintOptionsModal(false);
      setBulkPlanId('');
      setBulkQuantity(5);
      alert(`✅ PDF ${size} descargado exitosamente`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error al descargar PDF');
    }
  };

  const [showReprintModal, setShowReprintModal] = useState(false);
  const [recentVouchers, setRecentVouchers] = useState([]);
  
  // Estados para reportar pago
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentReceipt, setPaymentReceipt] = useState(null);

  const handleOpenReprintModal = async () => {
    setMenuOpen(false);
    setProcessing(true);
    try {
      const response = await api.get('/api/vouchers/my-recent');
      setRecentVouchers(response.data.vouchers);
      if (response.data.vouchers.length === 0) {
        alert('No hay fichas generadas recientemente');
      } else {
        setShowReprintModal(true);
      }
    } catch (error) {
      console.error('Error loading recent vouchers:', error);
      alert('Error al cargar fichas recientes');
    } finally {
      setProcessing(false);
    }
  };

  const handleSelectVoucherToReprint = (voucher) => {
    setGeneratedVoucher(voucher);
    setShowReprintModal(false);
    setShowVoucherModal(true);
  };

  const handleSearchVoucher = async () => {
    if (!searchCode || searchCode.trim() === '') {
      alert('Ingresa un código de ficha');
      return;
    }

    setProcessing(true);
    try {
      const response = await api.get(`/api/vouchers/search/${searchCode.trim()}`);
      setSearchResult(response.data);
    } catch (error) {
      console.error('Error searching voucher:', error);
      if (error.response?.status === 404) {
        alert('❌ Ficha no encontrada');
        setSearchResult(null);
      } else {
        alert('Error al buscar ficha: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmitPaymentReport = async (e) => {
    e.preventDefault();
    
    if (!paymentReceipt) {
      alert('Debes subir un comprobante');
      return;
    }

    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      alert('Ingresa un monto válido');
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('amount', paymentAmount);
      formData.append('notes', paymentNotes);
      formData.append('receipt', paymentReceipt);

      await api.post('/api/payment-reports', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('✅ Pago reportado exitosamente. Espera la verificación del administrador.');
      setShowPaymentModal(false);
      setPaymentAmount('');
      setPaymentNotes('');
      setPaymentReceipt(null);
      await loadSellerData(); // Actualizar datos
    } catch (error) {
      console.error('Error reporting payment:', error);
      alert('Error al reportar pago: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDark ? 'bg-gray-900' : 'bg-gray-100'
      }`}>
        <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${
          isDark ? 'border-blue-400' : 'border-blue-600'
        }`}></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' : 'bg-gray-100'
    }`}>
      {/* Header */}
      <header className={`shadow-sm sticky top-0 z-40 transition-colors duration-300 ${
        isDark ? 'bg-gray-800 border-b border-gray-700' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Saldo {seller?.seller_type === 'acumulativo' ? 'Acumulado' : 'Disponible'} ${seller?.current_balance?.toFixed(2) || '0.00'}
                </h1>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-600'
                }`}>
                  {APP_VERSION}
                </span>
              </div>
              <div className={`flex items-center gap-2 text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <span className={`font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Home</span>
                <span>/</span>
                <span>{pointOfSale?.name || 'TPV'}</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${
                  isDark 
                    ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={isDark ? 'Modo Claro' : 'Modo Oscuro'}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-900'
                }`}
              >
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Menu Lateral */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setMenuOpen(false)}>
          <div 
            className={`absolute right-0 top-0 h-full w-80 shadow-xl ${
              isDark ? 'bg-gray-800 border-l border-gray-700' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Menú</h2>
                <button
                  onClick={() => setMenuOpen(false)}
                  className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-900'}`}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setShowBulkModal(true);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg transition-colors ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <Package className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <div>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Impresión Múltiple</p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Generar lote de fichas</p>
                  </div>
                </button>

                <button
                  onClick={handleOpenReprintModal}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg transition-colors ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <Printer className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <div>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Reimprimir Ficha</p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Últimas 10 fichas generadas</p>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setShowSearchModal(true);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg transition-colors ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <Search className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  <div>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Buscar Ficha</p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Ver estatus de usuario</p>
                  </div>
                </button>

                {seller?.seller_type === 'por_saldo' && (
                  <>
                    <div className={`border-t my-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}></div>
                    
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        setShowPaymentModal(true);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg transition-colors ${
                        isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      }`}
                    >
                      <DollarSign className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                      <div>
                        <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Reportar Pago</p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Subir comprobante de pago</p>
                      </div>
                    </button>
                  </>
                )}

                <div className={`border-t my-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}></div>

                <button
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/profile');
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg transition-colors ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <UserCircle className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <div>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Mi Perfil</p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Ver información de mi cuenta</p>
                  </div>
                </button>

                <button
                  onClick={logout}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg transition-colors ${
                    isDark ? 'hover:bg-red-900/50 text-red-400' : 'hover:bg-red-50 text-red-600'
                  }`}
                >
                  <LogOut className="w-5 h-5" />
                  <div>
                    <p className="font-semibold">Cerrar Sesión</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerta de Saldo Insuficiente */}
      {seller?.seller_type === 'por_saldo' && seller?.current_balance <= 0 && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-semibold text-red-800">
                  ⚠️ Saldo insuficiente
                </p>
                <p className="text-sm text-red-700 mt-1">
                  No puedes generar fichas porque tu saldo es $0. Contacta al administrador para que te agregue saldo.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {plans.length === 0 ? (
          <div className="text-center py-12">
            <Package className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>No hay planes disponibles</h3>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Contacta al administrador</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {plans.map((plan, index) => {
              const isDisabled = processing || (seller?.seller_type === 'por_saldo' && seller?.current_balance <= 0);
              
              return (
                <button
                  key={plan.plan_id}
                  onClick={() => handleGenerateVoucher(plan)}
                  disabled={isDisabled}
                  className={`${getColorForIndex(index)} text-white p-8 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isDisabled ? 'grayscale' : ''}`}
                >
                  <div className="text-center">
                    <p className="text-5xl font-bold mb-4">$ {plan.price}</p>
                    <p className="text-lg font-semibold uppercase">{plan.plan_name}</p>
                    {seller?.seller_type === 'por_saldo' && seller?.current_balance <= 0 && (
                      <p className="text-xs mt-2 opacity-90">⚠️ Sin saldo</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de Ficha Generada */}
      {showVoucherModal && generatedVoucher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8">
            <h3 className="text-2xl font-bold text-center mb-6">✅ Ficha Generada</h3>
            
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="text-center">
                {generatedVoucher.pin ? (
                  <>
                    <p className="text-sm text-gray-600 mb-2">PIN</p>
                    <p className="text-3xl font-bold text-gray-900 mb-4">{generatedVoucher.pin}</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mb-2">Usuario</p>
                    <p className="text-3xl font-bold text-gray-900 mb-4">{generatedVoucher.code}</p>
                    
                    {generatedVoucher.password && (
                      <>
                        <p className="text-sm text-gray-600 mb-2">Contraseña</p>
                        <p className="text-2xl font-bold text-blue-600 mb-4">{generatedVoucher.password}</p>
                      </>
                    )}
                  </>
                )}
                
                <div className="border-t border-gray-300 pt-4 mt-4">
                  <p className="text-sm text-gray-600">Plan</p>
                  <p className="font-semibold text-gray-900">{generatedVoucher.plan_name}</p>
                </div>
              </div>

              {seller?.qr_print && (
                <div className="mt-6 bg-gray-50 border-2 border-gray-200 p-6 rounded-lg">
                  <p className="text-center text-sm font-semibold text-gray-700 mb-3">Escanea para conectar</p>
                  <div className="flex justify-center">
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <QRCodeSVG 
                        value={generatedVoucher.pin ? `PIN: ${generatedVoucher.pin}` : `Usuario: ${generatedVoucher.code}${generatedVoucher.password ? `\nContraseña: ${generatedVoucher.password}` : ''}`}
                        size={150}
                        level="H"
                        includeMargin={true}
                      />
                    </div>
                  </div>
                  <p className="text-center text-xs text-gray-500 mt-3">
                    {generatedVoucher.pin ? 'PIN de acceso' : 'Usuario y contraseña'}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  const message = generatedVoucher.password 
                    ? (generatedVoucher.pin ? `🎫 *Tu Ficha de Internet*\n\n*PIN:* ${generatedVoucher.pin}\n*Plan:* ${generatedVoucher.plan_name}\n\n¡Disfruta tu internet!` : `🎫 *Tu Ficha de Internet*\n\n*Usuario:* ${generatedVoucher.code}\n*Contraseña:* ${generatedVoucher.password}\n*Plan:* ${generatedVoucher.plan_name}\n\n¡Disfruta tu internet!`)
                    : `🎫 *Tu Ficha de Internet*\n\n*PIN:* ${generatedVoucher.code}\n*Plan:* ${generatedVoucher.plan_name}\n\n¡Disfruta tu internet!`;
                  
                  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                }}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
              >
                📱 WhatsApp
              </button>
              <button
                onClick={() => setShowVoucherModal(false)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reimprimir - Lista de últimas fichas */}
      {showReprintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold">🖨️ Reimprimir Ficha</h3>
              <button
                onClick={() => setShowReprintModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 mb-4">Selecciona la ficha que deseas reimprimir:</p>

            <div className="space-y-3">
              {recentVouchers.map((voucher, index) => (
                <div
                  key={voucher.id}
                  onClick={() => handleSelectVoucherToReprint(voucher)}
                  className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 cursor-pointer transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-bold bg-gray-200 text-gray-700 px-2 py-1 rounded">
                          #{index + 1}
                        </span>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${
                          voucher.status === 'generated' 
                            ? 'bg-blue-100 text-blue-700'
                            : voucher.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                        }`}>
                          {voucher.status === 'generated' && '⭕ Sin Usar'}
                          {voucher.status === 'active' && '🟢 En Uso'}
                          {voucher.status === 'used' && '✓ Usado'}
                        </span>
                      </div>
                      <p className="font-bold text-lg text-gray-900">{voucher.code}</p>
                      {voucher.password && (
                        <p className="text-sm text-gray-600">Pass: {voucher.password}</p>
                      )}
                      <p className="text-sm text-gray-600 mt-1">
                        {voucher.plan_name} - ${voucher.price}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(voucher.created_at).toLocaleString()}
                      </p>
                    </div>
                    <Printer className="w-6 h-6 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Impresión Múltiple */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-xl max-w-md w-full p-8 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>📦 Impresión Múltiple</h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Selecciona Plan
                </label>
                <select
                  value={bulkPlanId}
                  onChange={(e) => setBulkPlanId(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">Seleccionar...</option>
                  {plans.map((plan) => (
                    <option key={plan.plan_id} value={plan.plan_id}>
                      {plan.plan_name} - ${plan.price}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Cantidad de Fichas (máx. 50)
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={bulkQuantity}
                  onChange={(e) => setBulkQuantity(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div className={`border rounded-lg p-4 ${isDark ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                  <strong>Costo total:</strong> ${(plans.find(p => p.plan_id === bulkPlanId)?.price || 0) * bulkQuantity}
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkPlanId('');
                  setBulkQuantity(5);
                }}
                disabled={processing}
                className={`flex-1 px-4 py-3 rounded-lg font-semibold disabled:opacity-50 ${
                  isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkGenerate}
                disabled={processing || !bulkPlanId}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? 'Generando...' : 'Generar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Buscar Ficha */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold">🔍 Buscar Ficha</h3>
              <button
                onClick={() => {
                  setShowSearchModal(false);
                  setSearchCode('');
                  setSearchResult(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código de Usuario
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchCode}
                    onChange={(e) => setSearchCode(e.target.value)}
                    placeholder="Ingresa código..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchVoucher()}
                  />
                  <button
                    onClick={handleSearchVoucher}
                    disabled={processing}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50"
                  >
                    <Search className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {searchResult && (
                <div className="bg-gray-50 rounded-lg p-6 mt-4">
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Usuario</p>
                      <p className="text-xl font-bold text-gray-900">{searchResult.code}</p>
                    </div>
                    
                    {searchResult.password && (
                      <div>
                        <p className="text-sm text-gray-600">Contraseña</p>
                        <p className="text-lg font-bold text-blue-600">{searchResult.password}</p>
                      </div>
                    )}

                    <div className="border-t border-gray-300 pt-3">
                      <p className="text-sm text-gray-600">Plan</p>
                      <p className="font-semibold text-gray-900">{searchResult.plan_name}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600">Precio</p>
                      <p className="font-semibold text-gray-900">
                        {searchResult.price !== null && searchResult.price !== undefined ? `$${searchResult.price}` : 'No disponible'}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600">Estado</p>
                      <p className={`font-semibold ${
                        searchResult.status === 'generated' 
                          ? 'text-blue-600' 
                          : searchResult.status === 'active' 
                            ? 'text-green-600'
                            : searchResult.status === 'used' 
                              ? 'text-gray-600'
                              : 'text-yellow-600'
                      }`}>
                        {searchResult.status === 'generated' && '⭕ Sin Usar'}
                        {searchResult.status === 'active' && '🟢 En Uso'}
                        {searchResult.status === 'used' && '✓ Usado'}
                        {!searchResult.status && 'No disponible'}
                        {searchResult.status && !['generated', 'active', 'used'].includes(searchResult.status) && searchResult.status}
                      </p>
                    </div>

                    {searchResult.started_at && searchResult.status === 'active' && (
                      <div>
                        <p className="text-sm text-gray-600">Inicio de Uso</p>
                        <p className="text-sm text-gray-900">{new Date(searchResult.started_at).toLocaleString()}</p>
                      </div>
                    )}

                    <div>
                      <p className="text-sm text-gray-600">Generado</p>
                      <p className="text-sm text-gray-900">{new Date(searchResult.created_at).toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Botón Reimprimir */}
                  <div className="mt-4 pt-4 border-t border-gray-300">
                    {searchResult.status === 'generated' ? (
                      <button
                        onClick={() => {
                          setGeneratedVoucher(searchResult);
                          setShowSearchModal(false);
                          setShowVoucherModal(true);
                        }}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                      >
                        <Printer className="w-5 h-5" />
                        Reimprimir Ficha
                      </button>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-sm text-yellow-800 text-center">
                          {searchResult.status === 'active' && '⚠️ Esta ficha está en uso y no puede reimprimirse'}
                          {searchResult.status === 'used' && '⚠️ Esta ficha ya fue usada completamente'}
                          {searchResult.status === 'expired' && '⚠️ Esta ficha ha expirado'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reportar Pago */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8">
            <h3 className="text-2xl font-bold mb-6">💳 Reportar Pago</h3>
            
            <form onSubmit={handleSubmitPaymentReport} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monto a Recargar *
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-3 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    required
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comprobante de Pago *
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  required
                  onChange={(e) => setPaymentReceipt(e.target.files[0])}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Formatos: JPG, PNG, PDF (máx 5MB)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notas (Opcional)
                </label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: Transferencia BBVA, Folio: 123456"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Importante:</strong> Tu reporte será revisado por el administrador. El saldo se agregará una vez aprobado.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentAmount('');
                    setPaymentNotes('');
                    setPaymentReceipt(null);
                  }}
                  disabled={processing}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50"
                >
                  {processing ? 'Enviando...' : 'Enviar Reporte'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de opciones de impresión */}
      {showPrintOptionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl p-6 max-w-md w-full ${
            isDark ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              ✅ Fichas generadas exitosamente
            </h3>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              ¿En qué formato deseas imprimir las fichas?
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => handlePrintPDF('ticket')}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-semibold"
              >
                🎫 Ticket Térmico (80mm)
              </button>
              
              <button
                onClick={() => handlePrintPDF('carta')}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all font-semibold"
              >
                📄 Carta A4 (Múltiples fichas)
              </button>
              
              <button
                onClick={() => {
                  setShowPrintOptionsModal(false);
                  setBulkPlanId('');
                  setBulkQuantity(5);
                }}
                className={`w-full px-6 py-3 rounded-lg transition-all font-semibold ${
                  isDark
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {processing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-900 font-semibold">Procesando...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerSalesPage;
